import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  CheckContainedFill,
  Circle,
  Mail01,
  XCircleContainedFill,
} from 'lib/lucky-x-icons-2';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setEmailAddr,
  setValidEmailAddr,
} from 'store/features/raffleDetailsSlice';
import { errMsg } from '@/components/PageElement/ErrorMessage';
import { Raffle } from 'api/raffle/raffle_detail';

const EmailLabel = () => {
  const reg = useMemo(() => {
    return /^[a-zA-Z0-9_.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z0-9]{2,6}$/;
  }, []);
  const dispatch = useAppDispatch();
  const {
    validEmailAddr,
    raffleInfo,
    raffleRegisterResult: { validation },
    emailAddr,
  } = useAppSelector((state) => state.raffleDetails);
  const [value, setValue] = useState(emailAddr);
  const [isFocusEmail, setIsFocusEmail] = useState(false);
  const [raffle, setRaffle] = useState<Raffle>();

  useEffect(() => {
    setRaffle(raffleInfo);
  }, [raffleInfo]);

  const taskConfig = useCallback(
    (typeValue: string) => {
      return raffle?.requirements?.find((item) => item.type === typeValue);
    },
    [raffle?.requirements]
  );

  const emailVerifyIcon = useCallback(() => {
    if (validEmailAddr === 0) {
      return <Circle size={24} className="text-neutral-400" />;
    } else if (validEmailAddr === 1) {
      return <CheckContainedFill size={24} color="#98FF59" />;
    } else if (validEmailAddr === -1) {
      return <XCircleContainedFill size={24} className="text-warn-300" />;
    }
  }, [validEmailAddr]);

  const handleChangeEmail = useCallback(
    (e: any) => {
      if (!raffle?.my_entry && !validation) {
        dispatch(setValidEmailAddr(0));
        dispatch(setEmailAddr(''));
        setValue(e.target.value);
      }
    },
    [dispatch, raffle?.my_entry, validation]
  );

  const handleVerifyEmail = useCallback(() => {
    if (!raffle?.my_entry && !validation) {
      setIsFocusEmail(false);
      if (taskConfig('EMAIL')?.config?.necessary === 'optional' && !value) {
        return;
      }
      if (reg.test(value)) {
        dispatch(setValidEmailAddr(1));
        dispatch(setEmailAddr(value));
        localStorage.setItem('lastFillEmail',value);
      } else {
        dispatch(setValidEmailAddr(-1));
        errMsg('Invalid email address');
      }
    }
  }, [dispatch, raffle?.my_entry, reg, taskConfig, validation, value]);

  const render = useCallback(() => {
    return (
      <div>
        <div className="flex items-center justify-between text-base font-normal text-neutral-100">
          <div className="flex items-center gap-2">
            <Mail01 size={24} />
            <div>
              Email
              {taskConfig('EMAIL')?.config?.necessary === 'optional' &&
                '（optional）'}
            </div>
          </div>
        </div>

        <div className="relative  mt-2 flex items-center gap-3 rounded-2xl border-[1px] border-neutral-600 p-4">
          {!(
            taskConfig('EMAIL')?.config?.necessary === 'optional' &&
            validEmailAddr !== 1
          ) && <span className="shrink-0">{emailVerifyIcon()}</span>}
          <input
            type="text"
            className={classNames(
              'peer w-full border-none  bg-neutral-1000 text-white  placeholder:text-neutral-500 placeholder:peer-focus:hidden',
              {
                'caret-transparent': !!raffle?.my_entry || validation,
              }
            )}
            placeholder={isFocusEmail ? '' : 'Email address'}
            onChange={handleChangeEmail}
            value={value}
            onBlur={handleVerifyEmail}
            onFocus={() => {
              if (!raffle?.my_entry && !validation) {
                setIsFocusEmail(true);
              }
            }}
          />
        </div>
      </div>
    );
  }, [
    emailVerifyIcon,
    handleChangeEmail,
    handleVerifyEmail,
    isFocusEmail,
    raffle?.my_entry,
    taskConfig,
    validEmailAddr,
    validation,
    value,
  ]);

  if (
    !raffle?.requirement_is_email_open ||
    (raffle?.requirement_is_email_open &&
      taskConfig('EMAIL')?.config?.necessary === 'optional' &&
      (!!raffle?.my_entry || validation) &&
      !emailAddr)
  ) {
    return null;
  }

  return <div>{render()}</div>;
};

export default EmailLabel;
